// First import on purpose: the SDK patches fetch/XHR and the history API when
// it initialises, and anything imported before it would keep the unpatched
// versions -- so early requests would be missing from traces.
import '../../../lib/initFontAwesome'
import '../../shared/observability'
import '../../styles/globalRules.global.sass'
import '@stripe/stripe-js'
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'
import { addLocaleData } from 'react-intl'
import { AppWrapper } from '../../components/app_wrapper'
import { BATCH_JOB_LIST } from '../../components/worker/jobs'
import { createBrowserHistory } from 'history'
import { DownloadsListener } from '../../components/browser_downloads'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorMessage } from '../../components/error'
import { hot } from 'react-hot-loader'
import { ModalWarning } from '../../components/warnings'
import { Notifications } from '../../components/notifications'
import { PersistGate } from 'redux-persist/integration/react'
import { Provider } from 'react-redux'
import { render } from 'react-dom'
import { reportBoundaryError } from '../../shared/observability'
import { ConnectedRouter as Router } from 'connected-react-router'
import { ScrollbarMemory } from '../../components/scrollbar_memory/scrollbar_memory'
import BatchWorker from '../../components/worker/batchWorker'
import configureStore from './configureStore'
import de from 'react-intl/locale-data/de'
import en from 'react-intl/locale-data/en'
import es from 'react-intl/locale-data/es'
import ForgotPasswordModal from '../../components/forgot_password_modal'
import Init from './init'
import IntlGlobalProvider from '../../shared/IntlGlobalProvider'
import ja from 'react-intl/locale-data/ja'
import KeyboardShortcutsModal from 'spa/components/keyboard_shortcuts_modal'
import LocaleProvider from 'spa/components/locale_provider'
import LoginModal from 'spa/components/login_modal'
import NewSignUp from 'spa/components/newsignup'
import PlaybackErrorModal from '../../components/player/playbackErrorModal'
import PreSunsetModal from '../../pages/sunset/pre_sunset_modal'
import React, { StrictMode } from 'react'
import registerServiceWorker from '../../shared/registerServiceWorker'
import ResetPasswordModal from '../../components/reset_password_modal'
import Routes from './routes'
import ScrollToTop from '../../components/scroll/scroll_to_top'
import UpdatePasswordModal from '../../components/update_password_modal'

addLocaleData([...en, ...es, ...ja, ...de])
const history = createBrowserHistory()
const { store, persistor } = configureStore(history)

// expose store when run in Cypress
if (window?.Cypress) {
  window.store = store
  window.storage = window.localStorage
}

const renderApp = component => {
  render(
    <Provider store={store}>
      <Router history={history}>
        <PersistGate loading={null} persistor={persistor}>
          <LocaleProvider>
            <IntlGlobalProvider>
              <ErrorBoundary
                FallbackComponent={ErrorMessage}
                onError={reportBoundaryError}
              >
                <Init>
                  <BatchWorker
                    interval={15000}
                    jobs={BATCH_JOB_LIST(store.dispatch)}
                  >
                    <ScrollbarMemory />
                    <DownloadsListener />
                    <ScrollToTop>{component}</ScrollToTop>
                  </BatchWorker>
                </Init>
              </ErrorBoundary>
            </IntlGlobalProvider>
          </LocaleProvider>
        </PersistGate>
      </Router>
    </Provider>,
    document.getElementById('root')
  )
  hot(module)(component)
}

renderApp(
  <StrictMode>
    <AppWrapper>
      <Routes />
      <NewSignUp />
      <LoginModal />
      <ForgotPasswordModal />
      <KeyboardShortcutsModal />
      <UpdatePasswordModal />
      <ResetPasswordModal />
      <PlaybackErrorModal />
      <Notifications />
      <ModalWarning />
      <PreSunsetModal />
    </AppWrapper>
  </StrictMode>
)

registerServiceWorker()

if (module.hot) {
  module.hot.accept('../../apps/public/routes', () => {
    const newRoutes = require('../../apps/public/routes').default
    renderApp(newRoutes)
  })
  module.hot.accept('../../reducers', () => {
    const nextCreateRootReducer = require('../../reducers/index').default
    store.replaceReducer(nextCreateRootReducer(history))
  })
}
